import { z } from 'zod';

/**
 * Zod schemas for activity log entities
 * These provide runtime validation and type inference
 */

// ActivityLog schema — allow empty strings and null for optional fields (API/Prisma may return "" or null)
const optionalUrl = z.preprocess(
  (v) => (v == null || v === '' ? undefined : v),
  z.union([z.string().url(), z.string().regex(/^\//), z.literal('')]).optional()
);
const entityTypeEnum = ['property', 'tenant', 'vendor', 'owner', 'workOrder', 'bill', 'receivable', 'lease', 'application', 'lead'] as const;
const optionalEntityType = z
  .union([z.enum(entityTypeEnum), z.string()])
  .optional()
  .nullable()
  .transform(v => (v && entityTypeEnum.includes(v as (typeof entityTypeEnum)[number]) ? (v as (typeof entityTypeEnum)[number]) : undefined));

const timestampSchema = z.preprocess(
  (v) => (v == null ? undefined : v),
  z.union([z.string(), z.date(), z.number()]).optional()
).transform(ts => (ts == null ? new Date().toISOString() : typeof ts === 'number' ? new Date(ts).toISOString() : ts instanceof Date ? ts.toISOString() : String(ts)));

const activityTypeEnum = ['Financial', 'Maintenance', 'Leasing', 'Compliance', 'System', 'Communication'] as const;
const activityStatusEnum = ['Success', 'Warning', 'Error', 'Info'] as const;
export const activityLogSchema = z.object({
  id: z.string(),
  type: z.union([z.enum(activityTypeEnum), z.string()]).transform(t => (activityTypeEnum.includes(t as (typeof activityTypeEnum)[number]) ? t as (typeof activityTypeEnum)[number] : 'System')),
  title: z.string().transform(t => t?.trim() || 'Activity'),
  description: z.preprocess((v) => (v == null ? '' : v), z.string()).default(''),
  timestamp: timestampSchema,
  entityId: z.string().optional().nullable(),
  entityType: optionalEntityType,
  url: optionalUrl,
  user: z.string().optional().nullable(),
  status: z.union([z.enum(activityStatusEnum), z.string()]).optional().nullable().transform(s => (s && activityStatusEnum.includes(s as (typeof activityStatusEnum)[number]) ? s as (typeof activityStatusEnum)[number] : undefined)),
  metadata: z.record(z.unknown()).optional().nullable(),
  operatingOrganizationId: z.string().optional().nullable(),
  actorId: z.string().optional().nullable(),
  targetEntityId: z.string().optional().nullable(),
  targetEntityType: z.string().optional().nullable(),
  /** Present when the row originates from `FinancialLedgerAudit` (unified feed). */
  feedSource: z.enum(['activity_log', 'ledger_audit']).optional(),
  /** True when the row is backed by the append-only financial ledger audit sink. */
  ledgerVerified: z.boolean().optional(),
});

export type ActivityLog = z.infer<typeof activityLogSchema>;
